import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	Module,
	Button,
	Form,
	LookupInput,
	FormItem,
	FlexBox,
	FlexBoxBlock,
	RuntimeUtils,
	NumberUtils,
	TabPane,
	ParentBoundComponent,
} from 'ps-gui';

import {
	CellBubble,
	CellFiller,
	CellMainText,
	CellSubText,
	SummaryHeaderMainText,
	SummaryHeaderRowCount,
	SummaryHeaderSubText
} from './portalresultsetmodule';
import {
	ModuleBodySection,
	ModuleHeaderSection,
	ModuleHeader,
	ModuleHeaderBlock
} from '../blocks/modulesections';

import {
	getColumnText,
	getColumnTextMap,
	getFormItemLabel,
	getPhrase
} from '../data/phrases';
import UsageReportModel, {
	getAggregateOutputColumns,
	getSearchOutputColumns
} from './usagereportmodel';
import { withGlobalContext } from '../data/globalcontext';
import { ReactComponent as ModuleLogo } from '../svg/usagereport.svg';
import Message from '../blocks/message';
import {
	exportToCsv,
	getGlobalContextChangeChecker
} from './utils';
import Bonsai from '../components/bonsai';
import { renderDateTimeCell } from './commoncellrenderers';

const Wrapper = styled.div`
`;

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);

class UsageReportModule extends ParentBoundComponent {

	constructor() {
		super();
		this.setRoleId = this.setRoleId.bind(this);
		this.setDistributionPeriodId = this.setDistributionPeriodId.bind(this);

		this.exportToCsv = this.exportToCsv.bind(this);
		this.onTabSelected = this.onTabSelected.bind(this);
		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);
	}

	onTabSelected(tabId) {
		this.setModel(this.getModel().setSelectedTabId(tabId));
	}

	async setRoleId(roleId) {
		let model = this.getModel().setRoleId(roleId);
		this.setModel(await model.refreshSearch());
	}

	async setDistributionPeriodId(dpId) {
		let model = this.getModel().setDistributionPeriodId(dpId);
		this.setModel(await model.refreshSearch());
	}

	getModel() {
		return new UsageReportModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	componentDidMount() {
		const model = this.getModel();
		this.setModel(
			model
				.setSearchCustomResultProps(getCustomResultProps(model))
				.setAggregateCustomResultProps(getCustomResultProps(model))
		);
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	renderNoData() {
		return (
			<Message>
				{getPhrase('usageReportNoDataMessage')}
			</Message>
		);
	}

	exportToCsv() {
		if (this.getData().get('selectedTabId') === 'aggregate') {
			exportToCsv(this.getModel().getAggregate(), {
				outputColumns: getAggregateOutputColumns(true),
				columnTextMap: getColumnTextMap([
					'title',
					'subtitle',
					'recording_id'
				])
			});
		} else {
			exportToCsv(this.getModel().getSearch(), {
				outputColumns: getSearchOutputColumns(true),
				columnTextMap: getColumnTextMap([
					'title',
					'subtitle',
					'broadcaster',
					'is_sale',
					'is_rerun',
					'recording_id'
				])
			});
		}
	}

	render() {
		const model = this.getModel();
		const data = this.getData();
		const roleId = data.get('roleId');
		const roleLookup = data.get('roleLookup');
		const dpLookup = data.get('distributionPeriodLookup.' + roleId);
		const selectedTabId = data.get('selectedTabId') || 'search';
		const languageTimestamp = model.getLanguageTimestamp();
		if (!roleLookup) {
			return null;
		}
		if (roleLookup.isEmpty()) {
			return this.renderNoData();
		}
		return (
			<Wrapper>
				<ModuleHeaderSection>
					<ModuleHeaderBlock>
						<ModuleHeader>{getPhrase('UsageReport')}</ModuleHeader>
						<Form virtual horizontal>
							<FormItem label={getColumnText('role')} fixedWidth="470px" marginRight="30px">
								<LookupInput
									getText={roleLookup.getText}
									getRows={roleLookup.getRows}
									combo
									mandatory
									value={roleId}
									onChange={this.setRoleId}
									textRefreshTimestamp={languageTimestamp}
								/>
							</FormItem>
							{
								dpLookup &&
								<FormItem label={getColumnText('distribution_period')} fixedWidth="170px">
									<LookupInput
										getText={dpLookup.getText}
										getRows={dpLookup.getRows}
										combo
										mandatory
										value={data.get('distributionPeriodId')}
										onChange={this.setDistributionPeriodId}
										textRefreshTimestamp={languageTimestamp}
									/>
								</FormItem>

							}
						</Form>
					</ModuleHeaderBlock>
					<ModuleHeaderBlock>
						<ModuleLogo />
					</ModuleHeaderBlock>
				</ModuleHeaderSection>
				<ModuleBodySection>
					<RowCountEtc>
						<RowCount>
							<span className="label">{getPhrase('rowCount')}:</span>
							<span className="value">{data.getIn([
								selectedTabId,
								'data',
								'result',
								'data',
								'pager',
								'totalCount'
							])}</span>
						</RowCount>
						<Button
							type="medium"
							text={getPhrase('exportSelectionToCSV')}
							onAction={this.exportToCsv}
						/>
					</RowCountEtc>
					<TabPane
						selectedTabId={selectedTabId}
						tabs={tabs}
						onTabSelected={this.onTabSelected}
					>
						<Block24 />
					</TabPane>
					<Module
						key={selectedTabId}
						appState={data.get(selectedTabId)}
						onDataChange={newData => {
							this.setData(data.setIn([selectedTabId, 'data'], newData));
						}}
						customResultProps={data.get(selectedTabId + 'CustomResultProps')}
					//leftToolBar={this.renderCandidateListToolbar}
					//onResultIconMouseDown={this.onCandidateIconMouseDown}
					/>
				</ModuleBodySection>
			</Wrapper>
		);

	}
}

const Block24 = styled.div`
	height: 24px;
`;

const tabs = Immutable.fromJS([{
	id: 'search',
	text: () => getPhrase('inDetail')
}, {
	id: 'aggregate',
	text: () => getPhrase('aggregatedItems')
}]);

/* todo: hodit tyhle komponenty do jednoho mista */
const RowCountEtc = styled.div`
	margin-bottom: 24px;
`;

const RowCount = styled.div`
	display: inline-block;
	color: ${props => props.theme.disabledTextColor};
	font-size: 20px;
	margin-right: 40px;
	.label {
		font-weight: 400;
		margin-right: 5px;
	}
	.value {
		font-weight: 900;
	}
`;

const FlexCell = styled.div`
	display: flex;
	align-items: center;
`;

const FlexCellContent = styled.div`
	flex-grow: ${props => props.flexGrow};
	flex-shrink: ${props => props.flexShrink};
`;

const RecordingIdBlock = styled.span`
	margin-right: 8px;
	min-width: 70px;
	display: inline-block;
`;

const IsrcBlock = styled.span`
`;

const cellRendererMap = Object.freeze({
	'recording': (cell, row, colIndex) => {
		const subtitle = row.get('subtitle');
		let saleFlagId;
		let rerunFlagId;
		if (row.get('is_sale') === 'Y') {
			saleFlagId = 'usageReportFlag_P';
		}
		if (row.get('is_rerun') === 'Y') {
			rerunFlagId = 'usageReportFlag_R';
		}
		const recordingId = row.get('recording_id');
		const isrc = row.get('isrc');
		return (
			<FlexCell>
				<FlexCellContent flexGrow="1">
					<CellMainText>{cell}</CellMainText>
					{
						!!subtitle &&
						<CellSubText>{subtitle}</CellSubText>

					}
					<CellSubText>
						{
							recordingId && <RecordingIdBlock title={getFormItemLabel('recordingId')}>{recordingId}</RecordingIdBlock>
						}
						{
							isrc && (
								<IsrcBlock title={getFormItemLabel('isrc')}>{isrc}</IsrcBlock>
							)
						}
					</CellSubText>
				</FlexCellContent>
				{
					saleFlagId &&
					<FlexCellContent>
						<CellBubble className={saleFlagId} title={getPhrase(saleFlagId)}>
							{getPhrase(saleFlagId + '.short')}
						</CellBubble>
					</FlexCellContent>

				}
				{
					rerunFlagId &&
					<FlexCellContent>
						<CellBubble className={rerunFlagId} title={getPhrase(rerunFlagId)}>
							{getPhrase(rerunFlagId + '.short')}
						</CellBubble>
					</FlexCellContent>

				}
			</FlexCell>
		);
	},
	'station': (cell, row, colIndex) => {
		return (
			<>
				<CellMainText className="secondary">{cell}</CellMainText>
				<CellSubText>{row.get('broadcaster_name')}</CellSubText>
			</>
		);
	},
	'usage_date': renderDateTimeCell,
	'recording_type_id': (cell, row, colIndex) => {
		if (!cell) {
			return null;
		} else if (cell !== 'A' && cell !== 'V') {
			cell = 'Other';
		}
		const id = 'recordingType' + cell;
		return (
			<CellBubble className={id}>
				{getPhrase(id)}
			</CellBubble>
		);
	},
	ip_gross_amount: renderReward,
	np_gross_amount: renderReward
});

function renderReward(cell, row) {
	if (!cell) {
		return null;
	}
	const partyId = row.get('party_id');
	const rhPartyId = row.get('right_holder_party_id');
	const rhName = row.get('right_holder_name');
	if (partyId === rhPartyId || row.get('right_holder_role_type_id') !== 'ENS') {
		return cell;
	}
	return (
		<CellFiller>
			{cell}
			<RightHolderName title={rhName}>({rhName})</RightHolderName>
		</CellFiller>
	);
}

const RightHolderName = styled.div`
	position: absolute;
	top: 16px;
	left: 0px;
	width: 100%;
	text-align: left;
	font-size: 12px;
	color: #575757;
	font-weight: 500;
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
`;

function mapStationTexts(stations) {
	let res = {};
	stations.forEach(parent => {
		res[parent.get('parentId')] = parent.get('text');
		const children = parent.get('children');
		if (children) {
			children.forEach(child => {
				res[child.get('childId')] = child.get('text');
			});
		}
	});
	return res;
}

const defaultColumnWidths = {
	'recording_type_id': '140px',
	'time_used': '120px',
	'usage_date': '110px',
	'spins': '120px',
	'ip_gross_amount': '140px',
	'np_gross_amount': '140px',
};

function getCustomResultProps(model) {
	const data = model.getData();
	const stations = data.get('stations') || EMPTY_LIST;
	const stationTextMap = mapStationTexts(stations);

	return {
		cellRendererMap,
		renderSummaryHeader,
		defaultColumnWidths,
		columnFilters: {
			'recording_type_id': {
				type: 'lookup',
				lookup: 'audioVideo'
			},
			'time_used': {
				type: 'runtime',
				inputSize: 6,
			},
			'ip_gross_amount': {
				type: 'number',
				inputSize: 8,
			},
			'np_gross_amount': {
				type: 'number',
				inputSize: 8,
			},
			'spins': {
				type: 'number',
				inputSize: 4,
			},
			'recording': {
				type: 'text'
			},
			'usage_date': {
				type: 'date',
				withTime: true
			},
			'station': {
				type: 'custom',
				control: Bonsai,
				getText: value => {
					if (!value || value.size === 0) {
						return '';
					}
					return value.map(nodeId => stationTextMap[nodeId]).join(', ');
				},
				maxPopupHeight: '100vh',
				controlProps: {
					items: stations,
					maxHeight: '38vh',
					minWidth: '220px'
				}
			}
		},
		columnAlign: {
			ip_gross_amount: 'right',
			np_gross_amount: 'right',
			time_used: 'right',
			spins: 'right',
		}
	};
}

const formatNumber = NumberUtils.formatNumber;
const formatAmount = NumberUtils.getFixedDecimalFormatNumber(2);

function renderSummaryHeader(data) {
	const columns = data.get('columns') || EMPTY_LIST;
	const agg = data.get('aggregates') || EMPTY_MAP;
	return (
		<tr>
			<th colSpan={columns.size - aggDefinitions.length}>
				<FlexBox>
					<FlexBoxBlock flexGrow="1">
						<SummaryHeaderRowCount>
							<span className="label">{getPhrase('rowCount')}:</span>
							<span className="value">{data.getIn(['pager', 'totalCount'])}</span>
						</SummaryHeaderRowCount>
					</FlexBoxBlock>
					<FlexBoxBlock>
					</FlexBoxBlock>
				</FlexBox>
			</th>
			{
				aggDefinitions.map((ad, index) => {
					const name = ad.name;
					const value = ad.format(agg.get(name));
					return (
						<th key={index}>
							{
								value &&
								<>
									<SummaryHeaderMainText>{value}</SummaryHeaderMainText>
									<SummaryHeaderSubText>{getPhrase('usageReport.' + name + '.subText')}</SummaryHeaderSubText>
								</>

							}
						</th>
					);
				})
			}
		</tr>
	);
}

const aggDefinitions = [
	{ name: 'time_used_sum', format: RuntimeUtils.formatRuntime },
	{ name: 'spins_sum', format: formatNumber },
	{ name: 'ip_gross_amount_sum', format: formatAmount },
	{ name: 'np_gross_amount_sum', format: formatAmount },
];

export default withGlobalContext(UsageReportModule);
